import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { setSessionEndedHandler } from '../utils/sessionRetry';

const AuthModalContext = createContext(null);

export function AuthModalProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [, setReturnTo] = useState(null);
  // True when the modal opened because the session ended mid-task (shows the amber line).
  const [sessionEnded, setSessionEnded] = useState(false);
  const pendingSignIn = useRef(null);
  const { refreshSession } = useAuth();
  const navigate = useNavigate();

  const open = useCallback((options = {}) => {
    setReturnTo(options.returnTo ?? null);
    setIsAuthenticating(false);
    setIsOpen(true);
  }, []);

  // A call answered 401 NO_SESSION / INVALID_SESSION: open sign-in, and resolve true once the user is really signed in
  // so the caller can send the same call again. The page behind keeps its form.
  const openForSessionEnded = useCallback(() => {
    if (pendingSignIn.current) {
      return new Promise((resolve) => {
        const first = pendingSignIn.current;
        pendingSignIn.current = (v) => { first(v); resolve(v); };
      });
    }
    return new Promise((resolve) => {
      pendingSignIn.current = resolve;
      setSessionEnded(true);
      setIsAuthenticating(false);
      setIsOpen(true);
    });
  }, []);

  useEffect(() => setSessionEndedHandler(openForSessionEnded), [openForSessionEnded]);

  const settle = useCallback((signedIn) => {
    setSessionEnded(false);
    const resolve = pendingSignIn.current;
    pendingSignIn.current = null;
    if (resolve) resolve(signedIn);
  }, []);

  const close = useCallback(() => {
    settle(false);
    setIsOpen(false);
    setIsAuthenticating(false);
    setReturnTo(null);
  }, []);

  // Every auth method funnels through this once IT thinks sign-in
  // succeeded (a credentials POST returning ok, or an OAuth popup
  // closing) -- but none of those signals are proof by themselves (the
  // popup closing just as easily means the user cancelled, or the funnel
  // rejected the OAuth attempt). isAuthenticating blocks the form while
  // refreshSession() independently re-verifies the real session, and only
  // a real user coming back from it counts as success: only then do we
  // close and navigate. Returns {ok} so the caller (EmailStep/PasswordStep)
  // can show its own error and let the user try again otherwise, instead
  // of the modal silently closing as if sign-in had worked.
  const handleAuthenticated = useCallback(async () => {
    setIsAuthenticating(true);
    let authenticatedUser = null;
    try {
      authenticatedUser = await refreshSession();
    } finally {
      setIsAuthenticating(false);
    }

    if (!authenticatedUser) {
      return { ok: false };
    }

    settle(true);
    setIsOpen(false);
    setReturnTo((currentReturnTo) => {
      if (currentReturnTo) navigate(currentReturnTo);
      return null;
    });
    return { ok: true };
  }, [refreshSession, navigate, settle]);

  const value = useMemo(
    () => ({ isOpen, isAuthenticating, sessionEnded, open, close, handleAuthenticated }),
    [isOpen, isAuthenticating, sessionEnded, open, close, handleAuthenticated],
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
}

export function useAuthModal() {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within an AuthModalProvider');
  }
  return context;
}
